// SCR-STAFF-604 — جمع‌آوری امضا و وضعیت رضایت‌ها (US-DOC-041 تا 046): فهرست رضایت‌ها با وضعیت لحظه‌ای (Satisfied، Expired، RenewalRequired،
// Revoked)، اخذ حضوری (متن کامل، پیمایش، بندها، امضاکنندگان)، درخواست امضای از راه دور با لینک و OTP، امضای کاغذی اسکن‌شده، لغو از تاریخ و
// جزئیات با امضاها، گواهی ردپا و سند PDF امضاشده.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type DocConsentDetail, type DocConsentTemplateView, type DocConsentView, type DocSignatureRequestView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, EmptyState, ErrorState, FormDialog, JalaliDate, LoadingState, ReasonDialog, SampleBadge, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { ConsentStateChip, docKeys, SCR_STAFF_604 } from './common';
import { ConsentSignForm } from './ConsentSigning';
import { DocumentViewer } from './DocumentViewer';
import { extraFieldsOf, usePublishedConsents } from './hooks';

function TemplateValues({ template, values, onChange }: { template: DocConsentTemplateView | undefined; values: Record<string, string>; onChange: (values: Record<string, string>) => void }) {
  return (
    <>
      {extraFieldsOf(template).map((field) => (
        <TextField
          key={field.key}
          label={field.label}
          required={field.required}
          value={values[field.key] ?? ''}
          onChange={(event) => onChange({ ...values, [field.key]: event.target.value })}
          slotProps={{ htmlInput: { 'data-testid': `consent-value-${field.key}`, maxLength: 500 } }}
        />
      ))}
    </>
  );
}

/** امضای رضایت آماده‌شده (حضوری). */
export function SignPreparedConsent({ detail, onSigned }: { detail: DocConsentDetail; onSigned: (detail: DocConsentDetail) => void }) {
  const { tenantApi } = useTenantSession();
  const sign = useMutation({
    mutationFn: (input: { scrolledToEnd: boolean; acceptedClauses: string[]; signatures: { role: string; signerPatientId: string | null; image: string; strokes: unknown }[] }) =>
      unwrap(
        tenantApi.POST('/api/v1/documents/consents/{id}:sign', {
          params: { path: { id: detail.consent.id } },
          body: {
            scrolledToEnd: input.scrolledToEnd,
            acceptedClauses: input.acceptedClauses,
            signatures: input.signatures.map((signature) => ({ role: signature.role as 'Patient', signerPatientId: signature.signerPatientId, image: signature.image, strokes: signature.strokes as never })),
          },
        }),
      ),
    onSuccess: onSigned,
  });
  return (
    <ConsentSignForm
      text={detail.text}
      isSample={detail.consent.isSample}
      roles={detail.consent.requiredRoles}
      signers={detail.signers}
      busy={sign.isPending}
      error={sign.error}
      renderError={(error) => <ErrorState error={error} compact />}
      onSign={(input) => sign.mutate(input)}
    />
  );
}

function CollectDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const templates = usePublishedConsents();
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [prepared, setPrepared] = useState<DocConsentDetail | null>(null);
  const [signed, setSigned] = useState<DocConsentDetail | null>(null);
  const template = templates.data?.items.find((item) => item.id === templateId);
  const prepare = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/consents', { body: { templateId, patientId, values, appointmentId: null, providerId: null, serviceId: null, tabletSessionId: null } })),
    onSuccess: setPrepared,
  });
  const done = (detail: DocConsentDetail) => {
    setSigned(detail);
    void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
  };

  return (
    <FormDialog
      open
      titleKey="doc:consent.collect_title"
      screenId={SCR_STAFF_604}
      maxWidth="md"
      onSubmit={() => (prepared ? onClose() : templateId && prepare.mutate())}
      onClose={onClose}
      isDirty={prepared !== null && signed === null}
      isSubmitting={prepare.isPending}
      submitError={prepare.error}
      submitKey={prepared ? 'doc:actions.close' : 'doc:consent.prepare'}
    >
      {!prepared ? (
        <>
          <TextField select label={t('doc:consent.template')} value={templateId} onChange={(event) => setTemplateId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'consent-template' } }} required>
            {(templates.data?.items ?? []).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))}
          </TextField>
          {template?.isSample ? <SampleBadge /> : null}
          <TemplateValues template={template} values={values} onChange={setValues} />
        </>
      ) : signed ? (
        <Alert severity="success" data-testid="consent-signed">
          {t('doc:consent.signed_done')}
        </Alert>
      ) : (
        <SignPreparedConsent detail={prepared} onSigned={done} />
      )}
    </FormDialog>
  );
}

function RemoteDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const templates = usePublishedConsents();
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const template = templates.data?.items.find((item) => item.id === templateId);
  const send = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/documents/signature-requests', { body: { subjectType: 'Consent', subjectId: templateId, patientId, values, appointmentId: null, documentId: null, expiresAt: null, serviceId: null, signerPatientId: null } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog open titleKey="doc:consent.remote_title" screenId={SCR_STAFF_604} onSubmit={() => templateId && send.mutate()} onClose={onClose} isDirty={templateId !== ''} isSubmitting={send.isPending} submitError={send.error}>
      <Alert severity="info">{t('doc:consent.remote_hint')}</Alert>
      <TextField select label={t('doc:consent.template')} value={templateId} onChange={(event) => setTemplateId(event.target.value)} slotProps={{ htmlInput: { 'data-testid': 'consent-remote-template' } }} required>
        {(templates.data?.items ?? []).map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      <TemplateValues template={template} values={values} onChange={setValues} />
    </FormDialog>
  );
}

function DetailDialog({ consentId, onClose }: { consentId: string; onClose: () => void }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const fileId = useId();
  const [revoking, setRevoking] = useState(false);
  const [effective, setEffective] = useState('');
  const [viewing, setViewing] = useState(false);
  const detail = useQuery({
    queryKey: docKeys.consent(session?.tenantId, consentId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/consents/{id}', { params: { path: { id: consentId } } })),
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: docKeys.all(session?.tenantId) });
  const revoke = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/documents/consents/{id}:revoke', {
          params: { path: { id: consentId } },
          body: { reason, effectiveFrom: effective ? new Date(`${effective}T00:00:00`).toISOString() : null },
        }),
      ),
    onSuccess: () => {
      setRevoking(false);
      invalidate();
    },
  });
  const paper = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return unwrap(
        tenantApi.POST('/api/v1/documents/consents/{id}:paper', {
          params: { path: { id: consentId } },
          body: form as never,
          bodySerializer: (body: unknown) => body as FormData,
        }),
      );
    },
    onSuccess: invalidate,
  });
  const data = detail.data;

  return (
    <FormDialog open titleKey="doc:consent.detail_title" screenId={SCR_STAFF_604} onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false} submitKey="doc:actions.close" maxWidth="md">
      {detail.isPending ? <LoadingState rows={3} /> : null}
      {detail.error ? <ErrorState error={detail.error} /> : null}
      {data ? (
        <Stack spacing={2} data-testid="consent-detail">
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle1">{data.consent.templateName}</Typography>
            {data.consent.isSample ? <SampleBadge /> : null}
            <ConsentStateChip state={data.consent.state} testId="consent-detail-state" />
          </Stack>
          {data.consent.pdfSha256 ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              SHA-256: <bdi dir="ltr">{data.consent.pdfSha256}</bdi>
            </Typography>
          ) : null}
          {data.consent.documentId ? (
            <Button sx={{ alignSelf: 'flex-start' }} onClick={() => setViewing(true)} data-testid="consent-view-pdf">
              {t('doc:consent.view_pdf')}
            </Button>
          ) : null}
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2">{t('doc:consent.signatures')}</Typography>
            <List dense>
              {data.signatures.map((signature, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`${t(`doc:signer_role.${signature.role}`)} · ${signature.signerName}`}
                    secondary={
                      <>
                        {t(`doc:signature_method.${signature.method}`, { defaultValue: signature.method })} · <JalaliDate value={signature.signedAt} withTime />
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2">{t('doc:consent.trail')}</Typography>
            <List dense data-testid="consent-trail">
              {data.trail.map((event, index) => (
                <ListItem key={index} data-event={event.event}>
                  <ListItemText
                    primary={`${t(`doc:trail.${event.event}`, { defaultValue: event.event })} · ${event.actor}`}
                    secondary={
                      <>
                        <JalaliDate value={event.occurredAt} withTime />
                        {event.ip ? (
                          <>
                            {' · '}
                            <bdi dir="ltr">{event.ip}</bdi>
                          </>
                        ) : null}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          {data.consent.status === 'PendingSignatures' && access.can('doc.consent.collect') ? (
            <Stack spacing={1}>
              <Typography variant="subtitle2">{t('doc:consent.paper')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('doc:consent.paper_hint')}
              </Typography>
              <Button component="label" htmlFor={fileId} variant="outlined" sx={{ alignSelf: 'flex-start' }} disabled={paper.isPending}>
                {t('doc:consent.paper_upload')}
              </Button>
              <input id={fileId} type="file" hidden accept=".pdf,.jpg,.jpeg,.png" data-testid="consent-paper-file" onChange={(event) => event.target.files?.[0] && paper.mutate(event.target.files[0])} />
              {paper.error ? <ErrorState error={paper.error} compact /> : null}
            </Stack>
          ) : null}
          {(data.consent.status === 'Signed' || data.consent.status === 'RenewalRequired') && access.can('doc.consent.revoke') ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <TextField type="date" size="small" label={t('doc:consent.revoke_from')} value={effective} onChange={(event) => setEffective(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'consent-revoke-from' } }} />
              <Button color="error" onClick={() => setRevoking(true)} data-testid="consent-revoke">
                {t('doc:consent.revoke')}
              </Button>
            </Stack>
          ) : null}
          <ReasonDialog open={revoking} titleKey="doc:consent.revoke_title" messageKey="doc:consent.revoke_hint" onConfirm={(reason: string) => revoke.mutate(reason)} onCancel={() => setRevoking(false)} busy={revoke.isPending} error={revoke.error} />
          {viewing && data.consent.documentId ? <DocumentViewer documentId={data.consent.documentId} title={data.consent.templateName} onClose={() => setViewing(false)} /> : null}
        </Stack>
      ) : null}
    </FormDialog>
  );
}

export function PatientSignedConsentsPanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('doc');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const [collecting, setCollecting] = useState(false);
  const [remote, setRemote] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);
  const consents = useQuery({
    queryKey: docKeys.consents(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/consents', { params: { query: { patientId } } })),
  });
  const canCollect = access.can('doc.consent.collect');
  const requests = useQuery({
    queryKey: docKeys.signatureRequests(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/documents/signature-requests', { params: { query: { patientId } } })),
    enabled: canCollect,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/documents/signature-requests/{id}:cancel', { params: { path: { id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: docKeys.signatureRequests(session?.tenantId, patientId) }),
  });

  const columns: DataTableColumn<DocConsentView>[] = [
    {
      id: 'name',
      headerKey: 'doc:consent.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{row.templateName}</span>
          {row.isSample ? <SampleBadge /> : null}
        </Stack>
      ),
    },
    { id: 'state', headerKey: 'doc:consent.columns.state', cell: (row) => <ConsentStateChip state={row.state} testId={`consent-state-${row.id}`} /> },
    { id: 'signed', headerKey: 'doc:consent.columns.signed', cell: (row) => (row.signedAt ? <JalaliDate value={row.signedAt} withTime /> : '—') },
    { id: 'valid', headerKey: 'doc:consent.columns.valid_until', cell: (row) => (row.validUntil ? <JalaliDate value={row.validUntil} /> : '—') },
    { id: 'method', headerKey: 'doc:consent.columns.method', cell: (row) => (row.method ? t(`doc:consent_method.${row.method}`, { defaultValue: row.method }) : '—') },
    {
      id: 'actions',
      headerKey: 'doc:consent.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setDetail(row.id)} data-testid={`consent-open-${row.id}`}>
          {t('doc:actions.details')}
        </Button>
      ),
    },
  ];
  const requestColumns: DataTableColumn<DocSignatureRequestView>[] = [
    { id: 'status', headerKey: 'doc:consent.columns.state', cell: (row) => <span data-testid={`signature-request-${row.id}`}>{t(`doc:request_status.${row.status}`, { defaultValue: row.status })}</span> },
    { id: 'expires', headerKey: 'doc:forms.columns.expires', cell: (row) => <JalaliDate value={row.expiresAt} withTime /> },
    {
      id: 'actions',
      headerKey: 'doc:consent.columns.actions',
      cell: (row) =>
        row.status === 'Sent' || row.status === 'Opened' ? (
          <Button size="small" color="warning" onClick={() => cancel.mutate(row.id)}>
            {t('doc:actions.cancel')}
          </Button>
        ) : null,
    },
  ];

  return (
    <Stack spacing={2} data-screen-id={SCR_STAFF_604} data-testid="patient-signed-consents">
      {canCollect ? (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => setCollecting(true)} data-testid="consent-collect">
            {t('doc:consent.collect')}
          </Button>
          <Button variant="outlined" onClick={() => setRemote(true)} data-testid="consent-remote">
            {t('doc:consent.remote')}
          </Button>
        </Stack>
      ) : null}
      {consents.error ? <ErrorState error={consents.error} /> : null}
      {consents.data && consents.data.items.length === 0 ? <EmptyState titleKey="doc:consent.empty" /> : null}
      {consents.data && consents.data.items.length > 0 ? <DataTable captionKey="doc:consent.title" columns={columns} rows={consents.data.items} getRowId={(row) => row.id} /> : null}
      {canCollect && requests.data && requests.data.items.length > 0 ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('doc:consent.remote_requests')}
          </Typography>
          <DataTable captionKey="doc:consent.remote_requests" columns={requestColumns} rows={requests.data.items} getRowId={(row) => row.id} />
        </Paper>
      ) : null}
      {collecting ? <CollectDialog patientId={patientId} onClose={() => setCollecting(false)} /> : null}
      {remote ? <RemoteDialog patientId={patientId} onClose={() => setRemote(false)} /> : null}
      {detail ? <DetailDialog consentId={detail} onClose={() => setDetail(null)} /> : null}
    </Stack>
  );
}
